// Cost-breakdown (Explore → Project detail → Costs tab) magic values.
//
// The Costs tab renders a worked stamp-duty / financing example. The
// purchase quantum it works from is resolved from LIVE data
// (resolveSamplePrice in lib/exploreApi.ts) — dev-sales median → recent
// transacted median → district median → the labelled default below.
//
// Web and iOS (PROP-235 / PROP-243) MUST agree on these values so the
// same project shows the same illustrative figure on both surfaces.

/**
 * Clearly-labelled fallback quantum, in S$, used only when NO live price
 * signal resolves for a project (no dev-sales, no transactions, no
 * district median). Surfaced with source "default" so the UI can label
 * it as a generic figure, never as this project's price.
 *
 * 2.48M is the long-standing worked-example figure the Costs tab shipped
 * with; kept as the default so the example stays meaningful when the
 * data engine has no signal yet (common for pre-launch pipeline projects).
 */
export const COSTS_DEFAULT_SAMPLE_PRICE = 2_480_000;

/**
 * Trailing window (months, anchored on the project's most-recent sale)
 * over which the transacted-median fallback is computed. Keeps the
 * "recent" median from being dragged by stale sales years ago.
 */
export const COSTS_TXN_WINDOW_MONTHS = 12;

/**
 * Highest URA planning district. `districtToQueryParam` clamps to this so
 * an out-of-range district never fires a query the backend
 * (`normaliseDistrict`, 1..28) would reject — matches the server contract.
 */
export const COSTS_MAX_DISTRICT = 28;

// ── Worked-example assumptions (illustrative, not a quote) ─────────────
//
// The Costs tab renders a single worked stamp-duty / financing example for
// a first residential purchase. Only the purchase quantum is sourced from
// live data; the ratios below are fixed illustrative assumptions. Web and
// iOS (PROP-235 / PROP-243) must agree.
//
// BSD is NOT defined here — it lives in constants/calc.ts (`estBsd`,
// post-2023 6-band schedule mirroring the Go `constants.BSDResidentialBands`), the single
// web-side source of truth shared with PROP-236. ABSD is intentionally not
// modelled: it depends on the buyer's residency and existing property
// count, which an illustrative sample can't assume, and iOS omits it too.

/** Down-payment share of the purchase price (5% cash + 20% cash/CPF). */
export const COSTS_DOWN_PAYMENT_RATE = 0.25;

/** Loan-to-value share of the purchase price. */
export const COSTS_LTV_RATE = 0.75;

/** Stressed annual interest rate used for the TDSR monthly repayment. */
export const COSTS_STRESS_RATE = 0.04;

/** Loan tenure, in years, for the illustrative monthly repayment. */
export const COSTS_LOAN_TERM_YEARS = 25;
